/**
 * AppRemainingDialogs — Phase 2.2 (Plan §8) App.tsx slice #3.
 *
 * Bundles the remaining 11 dialog mounts into one sub-component so
 * `App.tsx` no longer scatters them across ~280 lines of JSX. Hook
 * ownership stays in `App.tsx`; this component is a pure renderer
 * receiving:
 *
 *   - 8 hook-encapsulated dialog results (BlockDefinition, InsertBlock,
 *     OverlapWarning, Print, BlockEditExit, ScaleListEdit,
 *     AnnotativeObjectScale, Xref)
 *   - 3 boolean-flag dialogs with App-scoped callbacks (WriteBlock,
 *     PurgeBlocks, RenameBlock) — these are NOT hook-encapsulated;
 *     a follow-up Phase 3.3 batch will introduce dedicated hooks
 *
 * Lazy chunk component refs are passed in to keep the React.lazy
 * boundary at the call site (so each dialog is still a separate JS
 * chunk fetched on first open).
 */

import { Suspense, type ComponentType } from "react";

import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";
import type { EntityPreview } from "@dxf-ai-editor/contracts";

import type { UseAnnotativeObjectScaleDialogResult } from "../hooks/useAnnotativeObjectScaleDialog";
import type { UseBlockDefinitionDialogResult } from "../hooks/useBlockDefinitionDialog";
import type { UseBlockEditExitDialogResult } from "../hooks/useBlockEditExitDialog";
import type { UseInsertBlockDialogResult } from "../hooks/useInsertBlockDialog";
import type { UseOverlapWarningDialogResult } from "../hooks/useOverlapWarningDialog";
import type { UsePrintDialogResult } from "../hooks/usePrintDialog";
import type { UseScaleListEditDialogResult } from "../hooks/useScaleListEditDialog";
import type { UseXrefDialogResult } from "../hooks/useXrefDialog";

interface PurgeCandidate {
  name: string;
  reason: string;
}

interface WriteBlockProps {
  onConfirm: (params: never) => void;
  onCancel: () => void;
  availableBlockNames: string[];
  selectedEntities: EntityPreview[];
  initialInsertionUnits: number;
}

interface PurgeBlocksProps {
  onConfirm: (blockNames: string[]) => void;
  onCancel: () => void;
  candidates: PurgeCandidate[];
}

interface RenameBlockProps {
  onConfirm: (params: never) => void;
  onCancel: () => void;
  blockNames: string[];
  validateRename: (oldName: string, newName: string) => unknown;
}

interface AppRemainingDialogsProps {
  // Hook-encapsulated dialogs
  blockDefinitionDialog: UseBlockDefinitionDialogResult;
  insertBlockDialog: UseInsertBlockDialogResult;
  overlapWarningDialog: UseOverlapWarningDialogResult;
  printDialog: UsePrintDialogResult;
  blockEditExitDialog: UseBlockEditExitDialogResult;
  scaleListEditDialog: UseScaleListEditDialogResult;
  annotativeObjectScaleDialog: UseAnnotativeObjectScaleDialogResult;
  xrefDialog: UseXrefDialogResult;

  // Boolean-flag dialogs (App-scoped callbacks)
  writeBlockDialogOpen: boolean;
  writeBlockProps: Omit<
    WriteBlockProps,
    "availableBlockNames" | "selectedEntities" | "initialInsertionUnits"
  >;
  purgeBlocksDialogOpen: boolean;
  purgeBlocksProps: Omit<PurgeBlocksProps, "candidates">;
  purgeCandidates: PurgeCandidate[];
  renameBlockDialogOpen: boolean;
  renameBlockProps: Omit<RenameBlockProps, "blockNames" | "validateRename">;
  validateRenameForDialog: RenameBlockProps["validateRename"];

  // Shared document reads
  geometryDocument: DrawingDocument;
  selectedEntities: EntityPreview[];

  // Lazy chunk component refs
  BlockDefinitionDialog: ComponentType<
    UseBlockDefinitionDialogResult["props"] & object
  >;
  InsertBlockDialog: ComponentType<
    UseInsertBlockDialogResult["props"] & object
  >;
  OverlapWarningDialog: ComponentType<
    UseOverlapWarningDialogResult["props"] & object
  >;
  PrintPreviewDialog: ComponentType<UsePrintDialogResult["props"] & object>;
  BlockEditExitDialog: ComponentType<
    UseBlockEditExitDialogResult["props"] & object
  >;
  ScaleListEditDialog: ComponentType<
    UseScaleListEditDialogResult["props"] & object
  >;
  AnnotativeObjectScaleDialog: ComponentType<
    UseAnnotativeObjectScaleDialogResult["props"] & object
  >;
  XrefManagerDialog: ComponentType<UseXrefDialogResult["props"] & object>;
  WriteBlockDialog: ComponentType<WriteBlockProps>;
  PurgeBlocksDialog: ComponentType<PurgeBlocksProps>;
  RenameBlockDialog: ComponentType<RenameBlockProps>;
}

export function AppRemainingDialogs(
  props: AppRemainingDialogsProps,
): JSX.Element {
  const {
    blockDefinitionDialog,
    insertBlockDialog,
    overlapWarningDialog,
    printDialog,
    blockEditExitDialog,
    scaleListEditDialog,
    annotativeObjectScaleDialog,
    xrefDialog,
    writeBlockDialogOpen,
    writeBlockProps,
    purgeBlocksDialogOpen,
    purgeBlocksProps,
    purgeCandidates,
    renameBlockDialogOpen,
    renameBlockProps,
    validateRenameForDialog,
    geometryDocument,
    selectedEntities,
    BlockDefinitionDialog,
    InsertBlockDialog,
    OverlapWarningDialog,
    PrintPreviewDialog,
    BlockEditExitDialog,
    ScaleListEditDialog,
    AnnotativeObjectScaleDialog,
    XrefManagerDialog,
    WriteBlockDialog,
    PurgeBlocksDialog,
    RenameBlockDialog,
  } = props;

  return (
    <>
      {blockDefinitionDialog.isOpen ? (
        <Suspense fallback={null}>
          <BlockDefinitionDialog {...blockDefinitionDialog.props} />
        </Suspense>
      ) : null}

      {insertBlockDialog.isOpen ? (
        <Suspense fallback={null}>
          <InsertBlockDialog {...insertBlockDialog.props} />
        </Suspense>
      ) : null}

      {overlapWarningDialog.isOpen && overlapWarningDialog.props ? (
        <Suspense fallback={null}>
          <OverlapWarningDialog {...overlapWarningDialog.props} />
        </Suspense>
      ) : null}

      {/* B11 — PrintPreviewDialog. Owned by `usePrintDialog`. */}
      {printDialog.isOpen ? (
        <Suspense fallback={null}>
          <PrintPreviewDialog {...printDialog.props} />
        </Suspense>
      ) : null}

      {blockEditExitDialog.isOpen ? (
        <Suspense fallback={null}>
          <BlockEditExitDialog {...blockEditExitDialog.props} />
        </Suspense>
      ) : null}

      {writeBlockDialogOpen ? (
        <Suspense fallback={null}>
          <WriteBlockDialog
            onConfirm={writeBlockProps.onConfirm}
            onCancel={writeBlockProps.onCancel}
            availableBlockNames={Object.keys(geometryDocument.blocks ?? {})}
            selectedEntities={selectedEntities}
            initialInsertionUnits={geometryDocument.insunits ?? 0}
          />
        </Suspense>
      ) : null}

      {/* PR-9 — SCALELISTEDIT dialog. */}
      {scaleListEditDialog.isOpen ? (
        <Suspense fallback={null}>
          <ScaleListEditDialog {...scaleListEditDialog.props} />
        </Suspense>
      ) : null}

      {/* PR-9 — Annotative Object Scale dialog. */}
      {annotativeObjectScaleDialog.props ? (
        <Suspense fallback={null}>
          <AnnotativeObjectScaleDialog {...annotativeObjectScaleDialog.props} />
        </Suspense>
      ) : null}

      {purgeBlocksDialogOpen ? (
        <Suspense fallback={null}>
          <PurgeBlocksDialog
            onConfirm={purgeBlocksProps.onConfirm}
            onCancel={purgeBlocksProps.onCancel}
            candidates={purgeCandidates}
          />
        </Suspense>
      ) : null}

      {renameBlockDialogOpen ? (
        <Suspense fallback={null}>
          <RenameBlockDialog
            onConfirm={renameBlockProps.onConfirm}
            onCancel={renameBlockProps.onCancel}
            blockNames={Object.keys(geometryDocument.blocks ?? {})}
            validateRename={validateRenameForDialog}
          />
        </Suspense>
      ) : null}

      {xrefDialog.isOpen ? (
        <Suspense fallback={null}>
          <XrefManagerDialog {...xrefDialog.props} />
        </Suspense>
      ) : null}
    </>
  );
}
